import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Eye, EyeOff, AlertCircle, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";

export function LoginForm() {
  const { login, isLoginLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [nonEnglishWarning, setNonEnglishWarning] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  // Check if text contains non-English characters
  const hasNonEnglish = (text: string) => {
    return /[^a-zA-Z0-9@._\-\s]/.test(text);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, password: value });
    setNonEnglishWarning(hasNonEnglish(value));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsLockOn(e.getModifierState("CapsLock"));
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsLockOn(e.getModifierState("CapsLock"));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.username || !formData.password) {
      setError("تکایە هەموو خانەکان پڕ بکەوە");
      return;
    }

    const result = await login(formData);
    if (!result.success) {
      setError(result.error || "ناوی بەکارهێنەر یان وشەی نهێنی هەڵەیە");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto h-20 w-20 rounded-2xl overflow-hidden shadow-lg">
            <img src="/icons/icon.svg" alt="قەندیل" className="h-full w-full object-cover" />
          </div>
          <div>
            <CardTitle className="text-2xl">پێشانگای قەندیل</CardTitle>
            <CardDescription className="mt-2">
              سیستەمی بەڕێوەبردنی کڕیارەکان
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username">ناوی بەکارهێنەر</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="ناوی بەکارهێنەر یان ئیمەیڵ"
                required
                disabled={isLoginLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">وشەی نهێنی</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handlePasswordChange}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyUp}
                  placeholder="وشەی نهێنی"
                  required
                  disabled={isLoginLoading}
                  className="pl-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoginLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>

              {/* Caps Lock Warning */}
              {capsLockOn && (
                <div className="flex items-center gap-2 text-sm text-warning">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>Caps Lock چالاکە</span>
                </div>
              )}

              {/* Non-English Warning */}
              {nonEnglishWarning && (
                <div className="flex items-center gap-2 text-sm text-warning">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>تکایە تەنها پیتی ئینگلیزی بەکاربهێنە</span>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoginLoading}>
              {isLoginLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  چوونەژوورەوە...
                </div>
              ) : (
                "چوونەژوورەوە"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
